<template>
  <div class="chat-dock">
    <!-- Globo de invitación (una vez por visita) -->
    <transition name="chat-dock-bubble">
      <div
        class="chat-dock__invite"
        v-if="showBubble && !isOpen && showLauncher"
        @click="open"
      >
        <button type="button" class="chat-dock__invite-close" @click.stop="dismissBubble" aria-label="Cerrar">×</button>
        ¿Te ayudo a buscar tu casa?
      </div>
    </transition>

    <!-- Lanzador flotante -->
    <button
      type="button"
      class="chat-dock__launcher"
      :class="{ 'chat-dock__launcher--hidden': isOpen || !showLauncher, 'chat-dock__launcher--intro': playIntro, 'chat-dock__launcher--reduced': reducedMotion }"
      @click="open"
      aria-label="Abrir el asistente de Avanza"
    >
      <span class="fa fa-comments"></span>
      <span class="chat-dock__dot" v-if="hasUnread"></span>
    </button>

    <!-- Panel -->
    <transition name="chat-dock-slide">
      <aside class="chat-dock__panel" v-if="isOpen">
        <header class="chat-dock__header">
          <div>
            <strong>Asistente Avanza</strong>
            <small>Te ayudo a encontrar tu hogar</small>
          </div>
          <div class="chat-dock__header-actions">
            <button type="button" @click="newConversation" title="Nueva consulta">
              <span class="fa fa-refresh"></span>
            </button>
            <button type="button" @click="close" aria-label="Cerrar el asistente">
              <span class="fa fa-times"></span>
            </button>
          </div>
        </header>

        <ChatThread class="chat-dock__thread" />

        <div class="chat-dock__footer">
          <ChatComposer />
          <nav class="chat-dock__links">
            <router-link to="/encontrar-un-hogar" @click.native="close">Encontrar tu hogar</router-link>
            <router-link to="/vender-alquilar" @click.native="close">Anuncia tu inmueble</router-link>
            <a :href="whatsappUrl" target="_blank" rel="noopener">
              <span class="fa fa-whatsapp"></span> WhatsApp
            </a>
          </nav>
        </div>
      </aside>
    </transition>

    <div class="chat-dock__backdrop" v-if="isOpen" @click="close"></div>
  </div>
</template>

<script>
import ChatThread from './ChatThread.vue'
import ChatComposer from './ChatComposer.vue'
import { shouldPlayIntro, markIntroSeen, prefersReducedMotion } from '../../utils/chatIntro'

export default {
  name: 'ChatDock',
  components: { ChatThread, ChatComposer },
  data () {
    return {
      whatsappUrl: 'https://wa.me/34653933298',
      seenCount: 0,
      scrolledPastHero: false,
      playIntro: false,
      showBubble: false,
      reducedMotion: prefersReducedMotion(),
      bubbleTimer: null
    }
  },
  computed: {
    isOpen () {
      return this.$store.state.chat.isPanelOpen
    },
    isHome () {
      return this.$route.name === 'Home'
    },
    // En la home el chat ya es el héroe: el lanzador solo aparece al bajar,
    // para que el asistente siga a mano el resto de la página.
    showLauncher () {
      return !this.isHome || this.scrolledPastHero
    },
    messageCount () {
      return this.$store.state.chat.messages.length
    },
    hasUnread () {
      return !this.isOpen && this.messageCount > this.seenCount
    }
  },
  created () {
    this.seenCount = this.messageCount
  },
  mounted () {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.onScroll()
    // Si el lanzador ya es visible al cargar (subpáginas), lanzar la intro.
    this.$nextTick(this.maybePlayIntro)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
    if (this.bubbleTimer) clearTimeout(this.bubbleTimer)
  },
  watch: {
    isOpen (open) {
      if (open) {
        this.seenCount = this.messageCount
        this.dismissBubble()
      }
    },
    messageCount () {
      if (this.isOpen) this.seenCount = this.messageCount
    },
    showLauncher (visible) {
      // En la home el lanzador aparece al bajar del héroe; ahí lanzamos la intro
      // si el héroe no la reprodujo antes.
      if (visible) this.maybePlayIntro()
    }
  },
  methods: {
    onScroll () {
      this.scrolledPastHero = window.scrollY > window.innerHeight * 0.7
    },
    maybePlayIntro () {
      if (!this.showLauncher || this.isOpen) return
      if (!shouldPlayIntro()) return
      markIntroSeen()
      this.playIntro = true
      this.showBubble = true
      this.bubbleTimer = setTimeout(() => { this.showBubble = false }, 5500)
    },
    dismissBubble () {
      this.showBubble = false
      if (this.bubbleTimer) { clearTimeout(this.bubbleTimer); this.bubbleTimer = null }
    },
    open () {
      this.dismissBubble()
      this.$store.dispatch('chat/openPanel')
    },
    close () {
      this.$store.dispatch('chat/closePanel')
    },
    newConversation () {
      this.$store.dispatch('chat/newConversation')
      this.seenCount = this.messageCount
    }
  }
}
</script>

<style scoped>
.chat-dock__launcher {
  position: fixed;
  bottom: 30px;
  right: 20px;
  width: 64px;
  height: 64px;
  border: none;
  border-radius: 50%;
  background: #E45726;
  color: #fff;
  font-size: 26px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  /* Por encima de la barra de cookies (z-index 9999 en App.vue), si no, tapa el clic. */
  z-index: 10000;
  transition: transform 0.2s, background 0.2s, opacity 0.2s;
}
.chat-dock__launcher:hover { background: #cc3e13; transform: scale(1.05); }
.chat-dock__launcher--hidden { opacity: 0; pointer-events: none; }

/* Intro una vez por visita: entrada deslizante + pulso */
.chat-dock__launcher--intro {
  animation: launcher-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both,
             launcher-pulse 1.8s ease 0.6s 2;
}
@keyframes launcher-in {
  from { opacity: 0; transform: translateY(30px) scale(0.6); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes launcher-pulse {
  0%, 100% { box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25); }
  45% { box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25), 0 0 0 12px rgba(228, 87, 38, 0.22); }
}
.chat-dock__launcher--intro.chat-dock__launcher--reduced {
  animation: launcher-fade 0.4s ease both;
}
@keyframes launcher-fade { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .chat-dock__launcher--intro { animation: launcher-fade 0.4s ease both; }
}

/* Globo de invitación */
.chat-dock__invite {
  position: fixed;
  bottom: 40px;
  right: 96px;
  max-width: 220px;
  background: #1D262D;
  color: #fff;
  padding: 12px 30px 12px 14px;
  border-radius: 14px;
  border-bottom-right-radius: 4px;
  font-size: 14px;
  line-height: 1.35;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  cursor: pointer;
  z-index: 10001;
}
.chat-dock__invite::after {
  content: '';
  position: absolute;
  right: -7px;
  bottom: 14px;
  border-left: 8px solid #1D262D;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
}
.chat-dock__invite-close {
  position: absolute;
  top: 4px;
  right: 8px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
.chat-dock-bubble-enter-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.chat-dock-bubble-leave-active { transition: opacity 0.2s ease; }
.chat-dock-bubble-enter { opacity: 0; transform: translateY(8px); }
.chat-dock-bubble-leave-to { opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .chat-dock-bubble-enter { transform: none; }
}

.chat-dock__dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #E45726;
}

.chat-dock__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(29, 38, 45, 0.45);
  z-index: 10001;
}

.chat-dock__panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  z-index: 10002;
  box-shadow: -8px 0 30px rgba(0, 0, 0, 0.18);
}

.chat-dock__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: #1D262D;
  color: #fff;
  flex: 0 0 auto;
}
.chat-dock__header strong { display: block; font-size: 15px; }
.chat-dock__header small { color: rgba(255, 255, 255, 0.7); font-size: 12px; }
.chat-dock__header-actions { display: flex; gap: 4px; }
.chat-dock__header-actions button {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 16px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
}
.chat-dock__header-actions button:hover { background: rgba(255, 255, 255, 0.12); }

.chat-dock__thread {
  flex: 1 1 auto;
  min-height: 0;
}

.chat-dock__footer {
  flex: 0 0 auto;
  padding: 12px 16px 16px;
  border-top: 1px solid #EDEFF1;
  background: #FAFBFC;
}
.chat-dock__links {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
  font-size: 12px;
}
.chat-dock__links a { color: #6B747B; }
.chat-dock__links a:hover { color: #E45726; }

/* Móvil: la ficha de inmueble tiene un CTA fijo abajo; subimos el lanzador
   y el globo para que no lo tapen. */
@media (max-width: 767px) {
  .chat-dock__launcher {
    bottom: 90px;
    width: 56px;
    height: 56px;
    font-size: 22px;
  }
  .chat-dock__invite {
    bottom: 98px;
    right: 84px;
    max-width: 190px;
  }
}

/* Escritorio: columna lateral fija (dos ventanas), sin velo */
@media (min-width: 992px) {
  .chat-dock__panel {
    width: var(--chat-w, 460px);
    box-shadow: -6px 0 24px rgba(0, 0, 0, 0.10);
    border-left: 1px solid #E7EAEC;
  }
  .chat-dock__backdrop { display: none; }
}

/* Transición */
.chat-dock-slide-enter-active, .chat-dock-slide-leave-active { transition: transform 0.25s ease; }
.chat-dock-slide-enter, .chat-dock-slide-leave-to { transform: translateX(100%); }
</style>
